import React, { useEffect, useMemo, useState } from 'react';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Services from './components/Services';
import Insurance from './components/Insurance';
import About from './components/About';
import Testimonials from './components/Testimonials';
import Contact from './components/Contact';
import Footer from './components/Footer';
import WhatsAppFloat from './components/WhatsAppFloat';
import EmiCalculator from './components/EmiCalculator';
import { brand } from './brandTheme';

const theme = createTheme({
  palette: {
    primary: {
      main: brand.primary,
      dark: brand.primaryDark,
      light: brand.primaryLight,
    },
    secondary: {
      main: brand.accent,
      dark: brand.accentDark,
    },
    background: {
      default: brand.surfaceWarm,
      paper: '#FFFFFF',
    },
    text: {
      primary: brand.slate,
      secondary: brand.muted,
    },
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
    h1: {
      fontWeight: 800,
      letterSpacing: '-0.03em',
      color: brand.slate,
      lineHeight: 1.15,
    },
    h2: { fontWeight: 700, letterSpacing: '-0.02em', color: brand.slate },
    h3: { fontWeight: 700, letterSpacing: '-0.02em', color: brand.slate },
    h4: { fontWeight: 600, color: brand.slate },
    h5: { fontWeight: 600, color: brand.slate },
    h6: { fontWeight: 600, color: brand.slate },
    body1: { lineHeight: 1.65 },
    body2: { lineHeight: 1.6 },
    button: { fontWeight: 600, letterSpacing: '0.01em' },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          padding: '10px 22px',
          boxShadow: 'none',
        },
        containedPrimary: {
          color: '#fff',
          backgroundColor: brand.primary,
          '&:hover': {
            backgroundColor: brand.primaryDark,
            boxShadow: 'none',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border: `1px solid ${brand.border}`,
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: 'none',
        },
      },
    },
  },
});

function App() {
  const [route, setRoute] = useState('home');

  useEffect(() => {
    const parseHash = () => {
      const hash = window.location.hash || '#/home';
      const segment = hash.replace(/^#\//, '');
      setRoute(segment || 'home');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    if (!window.location.hash) {
      window.location.hash = '#/home';
    }
    parseHash();
    window.addEventListener('hashchange', parseHash);
    return () => window.removeEventListener('hashchange', parseHash);
  }, []);

  const RouteComponent = useMemo(() => {
    switch (route) {
      case 'home':
        return Hero;
      case 'about':
        return About;
      case 'services':
        return Services;
      case 'insurance':
        return Insurance;
      case 'emi-calculator':
        return EmiCalculator;
      case 'contact':
        return Contact;
      case 'testimonials':
        return Testimonials;
      default:
        return Hero;
    }
  }, [route]);

  const CurrentPage = RouteComponent;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="App">
        <Navbar activeRoute={route} />
        <main
          className="main-content main-content--flush"
          style={{ paddingTop: route === 'home' ? 0 : 72 }}
        >
          <CurrentPage />
        </main>
        <Footer />
        <WhatsAppFloat />
      </div>
    </ThemeProvider>
  );
}

export default App;
